import React, { useState, useEffect } from 'react';

import dayjs from 'dayjs';
import _ from 'lodash';
import { Formik, Form, useFormikContext } from 'formik';

import useUsers from '../../context/UsersContext';
import useMenus from '../../context/MenuContext';
import Yup from '../../utils/yup';
import { PropertyType } from '../../config/property';
import masterDataService from '../../service/masterData.service';
import propertyService from '../../service/property.service';
import { NotificationType } from '../../config';
import addNotification from '../../utils/addNotification';

import FormFields from './FormFields';
import { FormHeaderUI, FormButtonPanelUI, DeleteDialogUI } from '../Interface';
import PropertyReports from './PropertyReports';
import FormsDataGrid from './FormsDataGrid';
import SelectListDatagrid from './SelectListDatagrid';
import GroupsDataGrid from './GroupsDataGrid';

export const PropertyForm = () => {
  const { getUser, user } = useUsers();
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [initValues, setInitValues] = useState({});
  const [refetchTable, setRefetchTable] = useState(true);
  const [viewField, setViewField] = useState(true);
  const [selectedValues, setSelectedValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [propertyOption, setPropertyOption] = useState([]);
  const [showOptionDataGrid, setShowOptionDataGrid] = useState(false);
  const [formProperty, setFormProperty] = useState([]);
  const [canEdit, setCanEdit] = useState(true);
  const [defaultValues, setDefaultValues] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [formPropertyGroup, setFormPropertyGroup] = useState([]);

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    getMenus();
    getFormTypeSubmenus();

    const def = {
      name: '',
      mandatory: false,
      type: '',
      visOrder: '',
      characterLength: '',
      expiryMonitoring: false,
      expiryAlertDays: null,
      emailNotification: false,
    };
    setDefaultValues(def);
  }, []);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('properties');
    setCanEdit(getCanEdit(submenuId));
  }, [menus]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(),
    mandatory: Yup.string(),
    type: Yup.string().required(),
    visOrder: Yup.number().nullable().positive().required(),
    characterLength: Yup.number()
      .nullable()
      .positive()
      .max(329)
      .test('characterLength-required', 'Kötelező', function (value) {
        const { type } = this.parent;
        if (type === PropertyType.LONGTEXT) {
          return value !== null && value !== undefined;
        }
        return true;
      }),
    expiryMonitoring: Yup.boolean(),
    expiryAlertDays: Yup.number()
      .nullable()
      .positive()
      .test('expiryAlertDays-required', 'Kötelező', function (value) {
        const { expiryMonitoring } = this.parent;
        if (expiryMonitoring) {
          return value !== null && value !== undefined;
        }
        return true;
      }),
    emailNotification: Yup.boolean(),
  });

  const handleSubmit = (values) => {
    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField) {
      handleInsertObject(values);
    }
  };

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!_.isEqual(selectedValues)) {
        valuesToForm();
        setPropertyOption(selectedValues.propertyOption ?? []);
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
      }
    } else {
      setInitValues(defaultValues);
      setOperatorsDisabled(true);
    }
  }, [selectedValues]);

  const valuesToForm = () => {
    const {
      name,
      mandatory,
      type,
      visOrder,
      formProperty: formValues,
      characterLength,
      expiryMonitoring,
      expiryAlertDays,
      emailNotification,
    } = selectedValues;

    const updatedFormProperty = formProperty.map((submenu) => ({
      ...submenu,
      checked: formValues?.some((form) => form.formId === submenu.subMenuId) || false,
    }));

    setInitValues({
      name,
      mandatory,
      type,
      visOrder,
      characterLength: characterLength ?? defaultValues.characterLength,
      expiryMonitoring,
      expiryAlertDays,
      emailNotification,
    });
    setFormProperty(updatedFormProperty);
  };

  const getFormTypeSubmenus = () => {
    masterDataService.formTypeSubmenus().then((data) => {
      setFormProperty(data);
    });
  };

  const getPropertyGroupsForSubmenus = async () => {
    if (selectedValues && selectedValues.formProperty && Array.isArray(selectedValues.formProperty)) {
      const formIds = selectedValues.formProperty.map((property) => property.formId);

      if (formIds.length > 0) {
        const data = await propertyService.getPropertyGroupsForSubmenus({ data: formIds });

        const updatedFormPropertyGroup = data.map((submenu) => ({
          ...submenu,
          checked: selectedValues.formPropertyGroup?.some((form) => form.groupId === submenu.id) || false,
        }));

        setFormPropertyGroup(updatedFormPropertyGroup);
      }
    }
  };
  useEffect(() => {
    if (selectedValues.formProperty?.length === 0) {
      setFormPropertyGroup([]);
    }
  }, [selectedValues.formProperty]);

  useEffect(() => {
    if (selectedValues && selectedValues.formProperty) {
      getPropertyGroupsForSubmenus();
    }
  }, [selectedValues, selectedValues.formProperty]);

  const updateObject = (values) => {
    const updData = {
      ...values,
      updatedBy: user.userId,
      characterLength: parseInt(values.characterLength),
      expiryAlertDays: parseInt(values.expiryAlertDays),
    };

    propertyService.updateProperty(updData, selectedValues.id).then((_simpleTask) => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });

      setRefetchTable(true);
      setEditButtonvisible(true);
      setViewField(true);
    });
  };

  const handleInsertObject = (values) => {
    const insertObject = {
      ...values,
      createdBy: user.userId,
      characterLength: parseInt(values.characterLength),
      expiryAlertDays: parseInt(values.expiryAlertDays),
    };

    propertyService
      .createProperty(insertObject)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });
        setRefetchTable(true);
        setAddField(true);
        setViewField(true);
        setSelectedValues(data);

        setRowSelectionModel(data.id);
      })

      .finally(() => {
        //@TODO loader
      });
  };

  const handleRemoveElement = async () => {
    const deleteData = {
      deletedBy: user.userId,
      deletedAt: dayjs(new Date()),
    };
    propertyService.deleteProperty(deleteData, selectedValues.id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues(defaultValues);
    });
  };

  const ShowOptionGrid = () => {
    const { values } = useFormikContext();
    useEffect(() => {
      if (values.type === PropertyType.COMBO) {
        setShowOptionDataGrid(true);
      } else {
        setShowOptionDataGrid(false);
      }
    }, [values.type]);
    return null;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-6 h-screen gap-2 p-4 bg-gray-100 mt-[-4rem] pt-[5rem]">
      <div className="col-span-1 lg:col-span-1 xl:col-span-3 overflow-hidden bg-white shadow sm:rounded-lg">
        <div className="h-full overflow-auto custom-scrollbar">
          <FormHeaderUI
            addClick={() => {
              setSelectedValues(defaultValues);
              setSelectedValues({});
              setViewField(false);
              setAddField(false);
            }}
            addDisabled={!viewField || !canEdit}
            modClick={() => {
              setViewField(false);
              setEditButtonvisible(false);
            }}
            modDisabled={!viewField || operatorsDisabled || !canEdit}
            deleteClick={() => {
              setShowDeleteDialog(true);
            }}
            deleteDisabled={!viewField || operatorsDisabled || !canEdit}
            copyClick={() => {
              setViewField(false);
              setAddField(false);
            }}
            copyDisabled={!viewField || operatorsDisabled || !canEdit}
          />

          <Formik
            initialValues={initValues}
            validationSchema={validationSchema}
            validateOnChange={false}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ values, setFieldValue, errors, validateField, resetForm }) => (
              <Form>
                <ShowOptionGrid />
                <FormFields
                  viewField={viewField}
                  values={values}
                  setFieldValue={setFieldValue}
                  errors={errors}
                  validateField={validateField}
                  selectedValues={selectedValues}
                />

                {addField && (
                  <div className="grid grid-cols-5 gap-6 p-2">
                    <div className="col-span-2">
                      <div>
                        <FormsDataGrid
                          formProperty={formProperty}
                          id={selectedValues?.id}
                          disabled={viewField}
                          setRefetchTable={setRefetchTable}
                          setSelectedValues={setSelectedValues}
                        />
                        <div className="mt-2">
                          <GroupsDataGrid
                            formPropertyGroup={formPropertyGroup}
                            id={selectedValues?.id}
                            disabled={viewField}
                            setRefetchTable={setRefetchTable}
                          />
                        </div>
                      </div>
                    </div>
                    {showOptionDataGrid && (
                      <div className="col-span-3">
                        <SelectListDatagrid
                          property={{ setPropertyOption, propertyOption }}
                          id={selectedValues?.id}
                          setRefetchTable={setRefetchTable}
                          user={user}
                          disabled={viewField}
                          selectedValues={selectedValues}
                          setSelectedValues={setSelectedValues}
                        />
                      </div>
                    )}
                  </div>
                )}

                {(!editButtonVisible || !addField) && (
                  <FormButtonPanelUI
                    onCancelClick={() => {
                      setViewField(true);
                      setEditButtonvisible(true);
                      setAddField(true);
                      resetForm();
                    }}
                  />
                )}
              </Form>
            )}
          </Formik>
        </div>
      </div>
      <div className="col-span-1 lg:col-span-1 xl:col-span-3 overflow-hidden bg-white shadow sm:rounded-lg  ">
        <div className="h-full overflow-auto custom-scrollbar">
          <PropertyReports
            refetch={{ refetchTable, setRefetchTable }}
            values={{ selectedValues, setSelectedValues }}
            viewField={viewField}
            operatorsDisabled={operatorsDisabled}
            rowSelection={{ rowSelectionModel, setRowSelectionModel }}
          />
        </div>
      </div>
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </div>
  );
};
